import { createReducer, on } from '@ngrx/store';
import { get, remove, save } from 'src/app/data/utils/localstorage.util';
import {
	Process,
	OnboardingProcessState,
	OnboardingProcess,
	StepRouteProgress,
} from 'src/app/models/state/onboarding-process-state.model';
import { OnboardingProcessActions } from '../actions/onboarding-process.actions';

const ONBOARDING_PROCESS_KEY: string = 'OnboardingProcessState';

export const initialState: OnboardingProcessState = {
	currentProcess: null,
	previousProcess: null,
	nextProcess: null,
	onboardingProgress: [],
	totalSteps: 0,
};

const getInitialState = (
	initialState: OnboardingProcessState
): OnboardingProcessState => {
	const stateFromLocalStorage = get<OnboardingProcessState>(
		ONBOARDING_PROCESS_KEY
	);
	if (stateFromLocalStorage !== null) {
		return stateFromLocalStorage;
	}

	saveStateToLocalStorage(initialState);

	return initialState;
};

export const onboardingProcessReducer = createReducer(
	getInitialState(initialState),
	on(OnboardingProcessActions.resetProcess, (): OnboardingProcessState => {
		remove(ONBOARDING_PROCESS_KEY);
		return initialState;
	}),
	on(
		OnboardingProcessActions.goNextProcess,
		(state, { canSkipNextProcess }): OnboardingProcessState => {
			let newState: OnboardingProcessState = {
				...state,
				previousProcess: state.currentProcess,
				currentProcess: state.nextProcess,
			};
			newState = getNextStepProcess(
				newState,
				canSkipNextProcess,
				newState.currentProcess
			);

			newState = {
				...newState,
				onboardingProgress: markPreviousStepRouteProcessAsCompleted(
					newState.onboardingProgress,
					newState.previousProcess
				),
			};

			const isCurrentStepRouteProcessFromNextStep =
				newState.currentProcess.stepNumber >
				newState.previousProcess.stepNumber;

			if (isCurrentStepRouteProcessFromNextStep) {
				newState = {
					...newState,
					onboardingProgress: markPreviousStepProcessAsCompleted(
						newState.onboardingProgress,
						newState.previousProcess.stepNumber
					),
				};
			}

			saveStateToLocalStorage(newState);

			return newState;
		}
	),
	on(
		OnboardingProcessActions.goPreviousProcess,
		(state, { canSkipNextProcess }): OnboardingProcessState => {
			const newPreviousStepProcess: Process = getPreviousStepProcess(
				state,
				state.previousProcess.stepNumber,
				state.previousProcess.stepRouteNumber
			);

			let newState: OnboardingProcessState = {
				...state,
				currentProcess: state.previousProcess,
				previousProcess: newPreviousStepProcess,
			};

			newState = getNextStepProcess(
				newState,
				canSkipNextProcess,
				newState.currentProcess
			);

			newState = {
				...newState,
				onboardingProgress: markCurrentStepRouteProcessAsInCompleted(
					newState.onboardingProgress,
					newState.currentProcess
				),
			};

			saveStateToLocalStorage(newState);
			return newState;
		}
	),
	on(
		OnboardingProcessActions.syncProcessWithRoute,
		(state, { route, canSkipNextProcess }): OnboardingProcessState => {
			const processForRoute = getProcessForRoute(state.onboardingProgress, route);
			const isAlreadyTheCurrentProcess =
				processForRoute &&
				state.currentProcess?.stepNumber === processForRoute.stepNumber &&
				state.currentProcess?.stepRouteNumber ===
					processForRoute.stepRouteNumber;

			if (!processForRoute || isAlreadyTheCurrentProcess) {
				return state;
			}

			let newState: OnboardingProcessState = {
				...state,
				currentProcess: processForRoute,
				previousProcess: getPreviousStepProcess(
					state,
					processForRoute.stepNumber,
					processForRoute.stepRouteNumber
				),
			};

			newState = getNextStepProcess(
				newState,
				canSkipNextProcess,
				processForRoute
			);

			newState = {
				...newState,
				onboardingProgress: markCurrentStepRouteProcessAsInCompleted(
					newState.onboardingProgress,
					processForRoute
				),
			};

			saveStateToLocalStorage(newState);
			return newState;
		}
	),
	on(OnboardingProcessActions.loadProcesses, (state, processesList) => {
		const { processes } = processesList;
		const currentStepProcess = processes[0];
		const currentStepRouteProcess = currentStepProcess.stepRoutesProgress[0];
		let newState: OnboardingProcessState = {
			...state,
			previousProcess: null,
			currentProcess: {
				stepNumber: currentStepProcess.stepNumber,
				stepRouteNumber: currentStepRouteProcess.stepRouteNumber,
				isRequired: currentStepRouteProcess.isRequired,
			},
			onboardingProgress: processes,
		};

		newState = getNextStepProcess(newState, false, newState.currentProcess);

		saveStateToLocalStorage(newState);
		return newState;
	}),
	on(OnboardingProcessActions.addTotalSteps, (state, payload) => {
		const newState: OnboardingProcessState = {
			...state,
			totalSteps: payload.totalSteps ?? 0,
		};

		saveStateToLocalStorage(newState);
		return newState;
	}),
	on(
		OnboardingProcessActions.updateOnboardingProgress,
		(state, processesList) => {
			const { processes, canSkipStepNextStep } = processesList;

			const previousStepProcess = processes[0];

			const previousStepRouteProcess =
				previousStepProcess.stepRoutesProgress[
					previousStepProcess.stepRoutesProgress.length - 1
				];

			const currentStepProcess = processes[1];
			const currentStepRouteProcess = currentStepProcess.stepRoutesProgress[0];

			let newState: OnboardingProcessState = {
				...state,
				currentProcess: {
					stepNumber: currentStepProcess.stepNumber,
					stepRouteNumber: currentStepRouteProcess.stepRouteNumber,
					isRequired: currentStepRouteProcess.isRequired,
				},
				previousProcess: {
					stepNumber: previousStepProcess.stepNumber,
					stepRouteNumber: previousStepRouteProcess.stepRouteNumber,
					isRequired: previousStepRouteProcess.isRequired,
				},
				onboardingProgress: processes,
			};

			newState = getNextStepProcess(
				newState,
				canSkipStepNextStep,
				newState.currentProcess
			);

			saveStateToLocalStorage(newState);
			return newState;
		}
	)
);

/** Busca el proceso al que pertenece una ruta ya navegada. */
function getProcessForRoute(
	onboardingProgress: Array<OnboardingProcess>,
	route: string
): Process {
	if (!route) {
		return null;
	}
	const path = route.split('?')[0];
	for (const stepProcess of onboardingProgress) {
		const stepRoute = stepProcess.stepRoutesProgress.find(
			progressRoute => progressRoute.route === path
		);
		if (stepRoute) {
			return {
				stepNumber: stepProcess.stepNumber,
				stepRouteNumber: stepRoute.stepRouteNumber,
				isRequired: stepRoute.isRequired,
			};
		}
	}
	return null;
}

function markCurrentStepRouteProcessAsInCompleted(
	onboardingProgress: Array<OnboardingProcess>,
	previousProcess: Process
): Array<OnboardingProcess> {
	return onboardingProgress.map(stepProcess => {
		if (stepProcess.stepNumber === previousProcess.stepNumber) {
			return {
				...stepProcess,
				stepRoutesProgress: markCurrentStepRouteAsInCompleted(
					stepProcess.stepRoutesProgress,
					previousProcess.stepRouteNumber
				),
			};
		}
		return stepProcess;
	});
}

function markCurrentStepRouteAsInCompleted(
	stepRoutes: Array<StepRouteProgress>,
	stepRouteNumber: number
): Array<StepRouteProgress> {
	return stepRoutes.map(stepRoute => {
		if (stepRoute.stepRouteNumber === stepRouteNumber) {
			return {
				...stepRoute,
				isCompleted: false,
			};
		}
		return stepRoute;
	});
}

/**
 * Espejo de getNextProcess: se mueve por posición dentro del arreglo en lugar
 * de restarle uno a stepNumber y a stepRouteNumber. El backend numera los pasos
 * con id_paso y las rutas con orden, y ninguno de los dos tiene por qué ser
 * consecutivo, así que restar uno puede dar un proceso que no existe.
 */
function getPreviousStepProcess(
	state: OnboardingProcessState,
	currentStepNumber: number,
	currentStepRouteNumber: number
): Process {
	const currentProgressProcess = state.onboardingProgress.find(
		stepProcess => stepProcess.stepNumber === currentStepNumber
	);

	if (!currentProgressProcess) {
		return null;
	}

	const indexOfCurrentStepRoute =
		currentProgressProcess.stepRoutesProgress.findIndex(
			stepRoute => stepRoute.stepRouteNumber === currentStepRouteNumber
		);

	const currentStepProcessHasPreviousStepRoutes = indexOfCurrentStepRoute > 0;

	if (currentStepProcessHasPreviousStepRoutes) {
		const previousStepRouteProcess =
			currentProgressProcess.stepRoutesProgress[indexOfCurrentStepRoute - 1];

		return {
			isRequired: previousStepRouteProcess.isRequired,
			stepNumber: currentProgressProcess.stepNumber,
			stepRouteNumber: previousStepRouteProcess.stepRouteNumber,
		};
	}

	const indexOfPreviousStepProgress =
		state.onboardingProgress.indexOf(currentProgressProcess) - 1;

	if (indexOfPreviousStepProgress < 0) {
		return null;
	}

	const previousStepProcess =
		state.onboardingProgress[indexOfPreviousStepProgress];
	const lastStepRouteFromPreviousStepProcess =
		previousStepProcess.stepRoutesProgress[
			previousStepProcess.stepRoutesProgress.length - 1
		];

	return {
		isRequired: lastStepRouteFromPreviousStepProcess.isRequired,
		stepNumber: previousStepProcess.stepNumber,
		stepRouteNumber: lastStepRouteFromPreviousStepProcess.stepRouteNumber,
	};
}

function getNextStepProcess(
	state: OnboardingProcessState,
	canSkipNextProcess: boolean,
	currentProcess: Process
): OnboardingProcessState {
	const newNextProcess = getNextProcess(state, currentProcess);

	let newState: OnboardingProcessState = {
		...state,
		nextProcess: newNextProcess,
	};

	if (newNextProcess && canSkipNextProcess && !newNextProcess.isRequired) {
		newState = {
			...newState,
			nextProcess: getNextProcess(newState, newNextProcess),
			onboardingProgress: markNextStepRouteProcessAsSkipped(
				newState.onboardingProgress,
				newNextProcess
			),
		};
	}
	return newState;
}

function markPreviousStepRouteProcessAsCompleted(
	onboardingProgress: Array<OnboardingProcess>,
	previousProcess: Process
): Array<OnboardingProcess> {
	return onboardingProgress.map(stepProcess => {
		if (stepProcess.stepNumber === previousProcess.stepNumber) {
			return {
				...stepProcess,
				stepRoutesProgress: markPreviousStepRouteAsCompleted(
					stepProcess.stepRoutesProgress,
					previousProcess.stepRouteNumber
				),
			};
		}
		return stepProcess;
	});
}

function markPreviousStepProcessAsCompleted(
	onboardingProcesses: Array<OnboardingProcess>,
	skippedStepNumber: number
): Array<OnboardingProcess> {
	return onboardingProcesses.map(process => {
		if (process.stepNumber === skippedStepNumber) {
			return {
				...process,
				isCompleted: true,
			};
		}
		return process;
	});
}

function markNextStepRouteProcessAsSkipped(
	onboardingProgress: Array<OnboardingProcess>,
	newNextProcess: Process
): Array<OnboardingProcess> {
	return onboardingProgress.map(stepProgress => {
		if (stepProgress.stepNumber === newNextProcess.stepNumber) {
			return {
				...stepProgress,
				stepRoutesProgress: markNextStepRouteAsSkipped(
					stepProgress.stepRoutesProgress,
					newNextProcess.stepRouteNumber
				),
			};
		}
		return stepProgress;
	});
}

function markNextStepRouteAsSkipped(
	stepRoutes: Array<StepRouteProgress>,
	stepRouteNumber: number
): Array<StepRouteProgress> {
	return stepRoutes.map(stepRoute => {
		if (stepRoute.stepRouteNumber === stepRouteNumber) {
			return {
				...stepRoute,
				wasSkipped: true,
			};
		}
		return stepRoute;
	});
}

function markPreviousStepRouteAsCompleted(
	stepRoutes: Array<StepRouteProgress>,
	stepRouteNumber: number
): Array<StepRouteProgress> {
	return stepRoutes.map(stepRoute => {
		if (stepRoute.stepRouteNumber === stepRouteNumber) {
			return {
				...stepRoute,
				isCompleted: true,
			};
		}
		return stepRoute;
	});
}

function saveStateToLocalStorage(state: OnboardingProcessState): void {
	save<OnboardingProcessState>(ONBOARDING_PROCESS_KEY, state);
}

export function getNextProcess(
	state: OnboardingProcessState,
	currentProcess: Process
): Process {
	const currentProgressProcess = state.onboardingProgress.find(
		onboardingProcess =>
			onboardingProcess.stepNumber === currentProcess.stepNumber
	);

	const indexOfCurrentStepRouter =
		currentProgressProcess.stepRoutesProgress.findIndex(
			stepRouter =>
				stepRouter.stepRouteNumber === currentProcess.stepRouteNumber
		);

	const indexOfNextStepRouteNumber = indexOfCurrentStepRouter + 1;

	const currentStepProcessHasMoreStepRoutes =
		indexOfNextStepRouteNumber <=
		currentProgressProcess.stepRoutesProgress.length - 1;

	if (currentStepProcessHasMoreStepRoutes) {
		const nextStepRoute =
			currentProgressProcess.stepRoutesProgress[indexOfNextStepRouteNumber];
		return {
			stepNumber: currentProcess.stepNumber,
			stepRouteNumber: nextStepRoute.stepRouteNumber,
			isRequired: nextStepRoute.isRequired,
		};
	}

	const indexOfCurrentStepProgress = state.onboardingProgress.indexOf(
		currentProgressProcess
	);

	const indexOfNextStepProgress = indexOfCurrentStepProgress + 1;
	const onboardingProcessesHasMoreStepsLeft =
		indexOfNextStepProgress <= state.onboardingProgress.length - 1;

	if (onboardingProcessesHasMoreStepsLeft) {
		const nextStepProcess = state.onboardingProgress[indexOfNextStepProgress];
		const nextStepRoute = nextStepProcess.stepRoutesProgress[0];
		return {
			stepNumber: nextStepProcess.stepNumber,
			stepRouteNumber: nextStepRoute.stepRouteNumber,
			isRequired: nextStepRoute.isRequired,
		};
	}

	return null;
}
